
import { GraduationCap } from "lucide-react";
import { FamilyProfileDialog } from "@/components/FamilyProfileDialog";
import { useLanguage } from "@/contexts/language";
import { t } from "@/utils/translations";
import { useMemo } from "react";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow 
} from "@/components/ui/table";

interface Grandparent {
  id: string;
  name: string;
  relation: 'maternal' | 'paternal';
}

interface GrandparentsInfoSectionProps {
  familyProfile: any;
  onProfileUpdate?: () => void;
}

export function GrandparentsInfoSection({ familyProfile, onProfileUpdate }: GrandparentsInfoSectionProps) {
  const { language } = useLanguage();

  // Get unique grandparents based on ID and name (as a fallback) to ensure no duplicates
  const uniqueGrandparents = useMemo(() => {
    if (!familyProfile?.grandparents || !Array.isArray(familyProfile.grandparents)) {
      return [];
    }
    
    // Use a Map with the ID as key to ensure uniqueness
    const uniqueMap = new Map<string, Grandparent>();
    
    familyProfile.grandparents.forEach((grandparent: Grandparent) => {
      // Only add if not already in the map
      // Use ID if available, otherwise create a composite key from name and relation
      const key = grandparent.id || `${grandparent.name}-${grandparent.relation}`;
      
      if (key && !uniqueMap.has(key)) {
        uniqueMap.set(key, grandparent);
      }
    });
    
    return Array.from(uniqueMap.values());
  }, [familyProfile?.grandparents]);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center">
        <GraduationCap className="h-5 w-5 mr-2 text-amber-500" />
        {t('family.grandparents', language)}
      </h3>
      
      {uniqueGrandparents.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{language === 'ja' ? '名前' : 'Name'}</TableHead>
              <TableHead>{t('family.grandparentRelation', language)}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {uniqueGrandparents.map((grandparent: Grandparent, index: number) => (
              <TableRow key={grandparent.id || `grandparent-${index}`}>
                <TableCell className="font-medium">{grandparent.name}</TableCell>
                <TableCell>
                  {grandparent.relation === 'maternal' 
                    ? t('family.maternal', language) 
                    : t('family.paternal', language)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center p-4 border border-dashed rounded-lg">
          <p className="text-muted-foreground mb-2">
            {language === 'ja' ? '祖父母はまだ追加されていません' : 'No grandparents added yet'}
          </p>
          <FamilyProfileDialog onComplete={onProfileUpdate} />
        </div>
      )}
    </div>
  );
}
